import { HttpStatusCode } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  signal,
  WritableSignal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NotificationStatus } from '@shared/enums/notification-status';
import { AppNotificationService, ModalService } from '@shared/services';
import { UtilityService } from '@shared/services/utility.service';
import moment from 'moment';
import { QueryOptions } from 'odata-query';
import { FileResponse } from 'src/app/web-api-client';
import { InvestmentJurisdiction } from '../../models';
import { InvestmentJurisdictionService } from '../../services/investment-jurisdiction.service';

@Component({
  standalone: false,
  selector: 'gstar-export-countries-information-modal',
  templateUrl: './export-countries-information-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportCountriesInformationModalComponent {
  private translation = inject(TranslateService);
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );
  private readonly modalService = inject(ModalService);
  private readonly utility = inject(UtilityService);
  private readonly appNotificationService = inject(AppNotificationService);

  @Input({ required: true }) modalId!: string;
  @Input() query: Partial<QueryOptions<InvestmentJurisdiction>> = {};
  @Input() totalRows: number = 0;
  isLoading: WritableSignal<boolean> = signal(false);

  confirm(): void {
    this.isLoading.set(true);

    if (this.totalRows === 0) {
      this.notifyNoCountriesAvailable();
      this.closeModal();
      return;
    }

    this.investmentJurisdictionService.get(this.buildAllRowsQuery()).subscribe({
      next: allRows => {
        const allCountryIds = this.extractCountryIds(allRows.items);

        if (allCountryIds.length === 0) {
          this.notifyNoCountriesAvailable();
          this.closeModal();
          return;
        }

        this.exportCountryInformation(allCountryIds);
      },
      error: () => {
        this.appNotificationService.notify(
          'admin.dashboard.failedToLoadCountries',
          '',
          NotificationStatus.ERROR
        );
        this.closeModal();
      },
    });
  }

  closeHandler(): void {
    if (!this.isLoading()) {
      this.modalService.close(this.modalId);
    }
  }

  private buildAllRowsQuery(): Partial<QueryOptions<InvestmentJurisdiction>> {
    return {
      filter: this.query.filter,
      orderBy: this.query.orderBy,
      top: this.totalRows,
      skip: 0,
    };
  }

  private extractCountryIds(rows: InvestmentJurisdiction[]): number[] {
    return Array.from(
      new Set(
        rows
          .map(row => row.country?.countryId)
          .filter((id): id is number => id !== undefined && id !== null)
      )
    );
  }

  private exportCountryInformation(countryIds: number[]): void {
    this.investmentJurisdictionService
      .exportCountryInformation(countryIds)
      .subscribe({
        next: (file: FileResponse) => {
          this.handleSuccessfulExport(file);
          this.closeModal();
        },
        error: () => {
          this.appNotificationService.notify(
            'admin.grid.exportCountryInformationErrorTitle',
            'admin.grid.exportCountryInformationErrorDescription',
            NotificationStatus.ERROR
          );
          this.closeModal();
        },
      });
  }

  private handleSuccessfulExport({
    data,
    fileName,
    status,
  }: FileResponse): void {
    if (status !== HttpStatusCode.Ok) {
      return;
    }

    const defaultName = this.translation.instant(
      'admin.dashboard.countryInformationFileNameDefault',
      { date: moment().format('MM-DD-YYYY') }
    );

    this.utility.download(fileName ?? defaultName, data);
    this.appNotificationService.notify(
      'admin.grid.exportCountryInformationSuccessTitle',
      '',
      NotificationStatus.SUCCESS
    );
  }

  private notifyNoCountriesAvailable(): void {
    this.appNotificationService.notify(
      'admin.dashboard.noCountriesAvailable',
      '',
      NotificationStatus.WARNING
    );
  }

  private closeModal(): void {
    this.isLoading.set(false);
    this.modalService.close(this.modalId);
  }
}
